import { useState, type FC, type JSX } from 'react';
import { Call } from '../../types';
import { Bot, CheckCircle, Phone, Send, Users, X } from 'lucide-react';
import LiveTranscript from './LiveTranscript';
import type { UseRealtimeTranscriptReturn } from '../../hooks/useRealtimeTranscript';

interface LiveCallMonitorProps {
  getCallDuration: (call: Call) => string;
  call: Call;
  transcriptHook: UseRealtimeTranscriptReturn;
  onClose: () => void;
}

const LiveCallMonitor:FC<LiveCallMonitorProps> = ({ 
    getCallDuration, 
    call, 
    transcriptHook, 
    onClose 
}: LiveCallMonitorProps): JSX.Element => {
    const [isTakingOver, setIsTakingOver] = useState(false);
   

  const handleTakeOver = () => {
    setIsTakingOver(true);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <h2 className="text-2xl font-bold">Live Call Monitor</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-green-100 mb-1">Patient</p>
              <p className="font-semibold text-lg">{call.patientName}</p>
            </div>
            <div>
              <p className="text-green-100 mb-1">Duration</p>
              <p className="font-mono font-bold text-lg">{getCallDuration(call)}</p>
            </div>
            <div>
              <p className="text-green-100 mb-1">Purpose</p>
              <p className="font-semibold text-lg capitalize">{call.purpose}</p>
            </div>
          </div>
        </div>

        {/* Main Content - Split View */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Side - Real-time Transcript */}
          <div className="flex-1 border-r border-gray-200">
            <LiveTranscript callId={call.id} transcriptHook={transcriptHook} />
          </div>

          {/* Right Side - Static Call Info */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Call Information</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Call ID:</span>
                    <span className="font-mono text-gray-900">{call.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type:</span>
                    <span className="font-medium text-gray-900 capitalize">{call.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Urgency:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      call.urgencyLevel === 'emergency' ? 'bg-red-100 text-red-800' :
                      call.urgencyLevel === 'high' ? 'bg-orange-100 text-orange-800' :
                      call.urgencyLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {call.urgencyLevel}
                    </span>
                  </div>
                </div>
              </div>

              {/* Previous Messages (if any from call history) */}
              {call.messages && call.messages.length > 0 && (
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Previous Messages</h3>
                  <div className="space-y-3">
                    {call.messages.map((message, index) => (
                      <div
                        key={message.id || index}
                        className="p-3 rounded bg-gray-50 text-sm"
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          {message.speaker === 'patient' ? (
                            <Users className="w-3 h-3" />
                          ) : (
                            <Bot className="w-3 h-3" />
                          )}
                          <span className="text-xs font-semibold uppercase text-gray-600">
                            {message.speaker}
                          </span>
                        </div>
                        <p className="text-gray-700">{message.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-gray-200 p-6 bg-white">
          {!isTakingOver ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Bot className="w-5 h-5 text-green-600" />
                <span>AI is currently handling this call</span>
              </div>
              <button
                onClick={handleTakeOver}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center space-x-2"
              >
                <Phone className="w-5 h-5" />
                <span>Take Over Call</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 text-green-600 font-semibold">
                <CheckCircle className="w-5 h-5" />
                <span>You are now connected to this call</span>
              </div>
              <div className="flex space-x-2">
                <input
                  type="text"
                  placeholder="Type your message or speak..."
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  autoFocus
                />
                <button className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2">
                  <Send className="w-5 h-5" />
                  <span>Send</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LiveCallMonitor;